/**
 * Push Notification Broadcast History
 * Shows previously sent broadcast notifications
 */
import { Clock, Zap, XCircle, Radio } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const formatDate = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-FJ', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function BroadcastHistory({ history = [] }) {
  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-foreground">Broadcast History</CardTitle>
        <CardDescription>Previously sent broadcast notifications</CardDescription>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Radio className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No broadcasts sent yet</p>
            <p className="text-sm">Click "Broadcast" to send your first notification</p>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((broadcast, idx) => (
              <div 
                key={idx}
                className="p-4 rounded-lg border border-border bg-muted/20"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">{broadcast.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{broadcast.body}</p>
                  </div>
                  <Badge variant="outline" className="ml-2">
                    {broadcast.sent}/{broadcast.total_subscribers}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(broadcast.sent_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="w-3 h-3 text-green-500" />
                    {broadcast.webpush_delivered} instant
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-yellow-500" />
                    {broadcast.polling_queued} queued
                  </span>
                  {broadcast.failed > 0 && (
                    <span className="flex items-center gap-1 text-red-500">
                      <XCircle className="w-3 h-3" />
                      {broadcast.failed} failed
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
